import i18n from '@/lang';
import { message } from 'ant-design-vue';
import axios from 'axios';
import { requestOpen } from '@/utils';

const ErrorMsg = {
  400: i18n.t('common.error.400'),
  401: i18n.t('common.error.401'),
  403: i18n.t('common.error.403'),
  404: i18n.t('common.error.404'),
  405: i18n.t('common.error.405'),
  408: i18n.t('common.error.408'),
  500: i18n.t('common.error.500'),
  501: i18n.t('common.error.501'),
  502: i18n.t('common.error.502'),
  503: i18n.t('common.error.503'),
  504: i18n.t('common.error.504'),
  505: i18n.t('common.error.505'),
  10011: i18n.t('v2.noPermission'),
  20005: i18n.t('v2.surveyFail'),
  20006: i18n.t('v2.surveyFail')
};

// 创建一个 axios 实例
const httpFetch = axios.create({
  timeout: 120 * 1000 // 请求超时时间
});

// 请求拦截器
httpFetch.interceptors.request.use(
  config => {
    return config;
  },
  error => {
    // 发送失败
    return Promise.reject(error);
  }
);

// 响应拦截器
httpFetch.interceptors.response.use(
  response => {
    if (response.data.code === 3 || response.data.code === 401) {
      message.info(i18n.t('common.loginExpired'), 1, () => {
        window?.sensors?.logout();
        if (process.env.VUE_APP_CLOUD_DEFAULT_LANGUAGE === 'zh') {
          const returnURL = new URL(window.location.href);
          returnURL.hash = `v2`;
          const url = new URL('/webproxy/sso/logout', location.href);
          url.searchParams.set('back', returnURL.href);
          window.location.href = url.href;
        } else {
          requestOpen({
            api: '/webproxy/sso/logout/intl',
            method: 'GET'
          });
          window.location.href = '/admin3/login/exit';
        }
      });
    }

    return response;
  },
  error => {
    if (error.response && error.response.status == 403) {
      const ret = error.response.data;
      if (ret.code == -1) {
        const href = ret.data.ssoJump + '?rewrite=off&back=' + encodeURIComponent(location.href);
        location.href = href;
        return;
      }
    }

    const msg = error.response && error.response.status ? ErrorMsg[error.response.status] : ErrorMsg['400'];
    return Promise.resolve({ code: -1, msg: msg });
  }
);

/**
 *
 * @DATA {
 *  api: url,
    method: method,
    params:{}, //post和put的body参数,get和delete请求不用传
    querys:{},//url上的参数
    module: "default"  //api模块，用于区分service 部署的ip,不传默认default，非/service/api/的模块都需要传该参数，巡检--inspection，日志--logbiz，终端管理--pyservice
 * @param {*} ops
 * @param {*} proxyType
 */
function requestHttp(data, ops = { showMsg: true }, proxyType = 'common') {
  try {
    if (!data) {
      message.error(i18n.t('common.fillParamsError'));
      return;
    }

    if (data && !data.api) {
      message.error(i18n.t('common.fillApiError'));
      return;
    }

    if (data && !data.method) {
      message.error(i18n.t('common.fillApiError'));
      return;
    }

    if (!data.module) {
      data.module = 'default';
    }

    const url = encodeURI(`/webproxy/${proxyType}/api?${data.api.split('?')[0]}`);
    const options = {
      method: 'POST',
      contentType: 'application/json',
      ...ops
    };
    if (data.showMsg !== undefined) {
      options.showMsg = data.showMsg;
    }

    if (!data['querys']) {
      data['querys'] = {};
    }

    data['querys']['lang'] = window.localStorage.getItem('lang') || 'cn';

    return new Promise((resolve, reject) => {
      httpFetch
        .post(url, data, options)
        .then(response => {
          if (response && response.data) {
            if (data.showMsg && response.data?.code !== 0) {
              message.error(response.data?.msg || ErrorMsg['400']);
            }
            // 2.0工作台遇到权限修改的时候做出跳到首页
            if (
              (window.location.href.indexOf('projectDetailV2') || window.location.href.indexOf('bench') > 0) &&
              (response.data?.code === 10011 || response.data?.code === 20006 || response.data?.code === 20005)
            ) {
              message.error(ErrorMsg[response.data?.code]);

              setTimeout(() => {
                window.location.href = `${window.location.href.split('#/')[0]}#/v2`;
              }, 500);
            }
            resolve(response.data);
          } else {
            reject({ msg: i18n.t('common.noReturn') });
          }
        })
        .catch(error => {
          reject(error);
        });
    });
  } catch (err) {
    console.error(err);
  }
}

function requestHttpUpload(data, uploadProgrss) {
  const url = '/webproxy/common/file/upload_by_form';
  var fd = new FormData();
  fd.append('file', data.file);
  fd.append('uploadData', data.uploadData);
  return new Promise((resolve, reject) => {
    axios
      .post(url, fd, uploadProgrss)
      .then(res => {
        resolve(res.data);
      })
      .catch(error => {
        reject(error);
      });
  });
}

const enetApiUrl =
  __APP_BRAND__ === 'lysora'
    ? {}
    : {
        'https://cloud-as.lysoratech.com': 'https://enet-as.lysoratech.com', //海外生产（亚洲云）
        'https://cloud-eu.lysoratech.com': 'https://enet-eu.lysoratech.com', //海外生产（欧洲云）
        'https://cloud-me.lysoratech.com': 'https://enet-eu.lysoratech.com', //海外生产（欧洲云）
        'https://cloud-a1.lysoratech.com': 'https://enet-eu.lysoratech.com', //海外生产（欧洲云）
        'https://cloud-la.lysoratech.com': 'https://enet-eu.lysoratech.com', //海外生产（欧洲云）
        'https://cloud-us.lysoratech.com': 'https://enet-us.lysoratech.com', //海外生产（美洲云）
        'https://cloud-uk.lysoratech.com': 'https://enet-uk.lysoratech.com', //海外生产（英国云）
        'https://cloud-ap.lysoratech.com': 'https://enet-ap.lysoratech.com' //海外生产（澳洲云）
      };

export { enetApiUrl, requestHttp, requestHttpUpload };
